/**
 * Debug overlay for device tests (task: test spike on desktop and phone).
 * Shows backend, fps, frame times, draw calls and captured errors,
 * and copies a plain-text report for the HQ Inbox.
 */
import { useEffect, useState } from 'react'
import { perf, perfReport } from './perf'
import { flags } from '../config/flags'
import { fixedLevel } from '../quality/store'

function link(params: Record<string, string | null>) {
  const url = new URL(window.location.href)
  for (const [k, v] of Object.entries(params)) {
    if (v === null) url.searchParams.delete(k)
    else url.searchParams.set(k, v)
  }
  return url.toString()
}

export function Hud() {
  const [, tick] = useState(0)
  const [open, setOpen] = useState(() => window.innerWidth > 640)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 250)
    return () => window.clearInterval(id)
  }, [])

  const copy = async () => {
    const text = perfReport()
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      window.prompt('Copy this report:', text)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const fpsClass = perf.fps >= 55 ? 'ok' : perf.fps >= 40 ? 'warn' : 'bad'

  return (
    <div className="hud" data-open={open}>
      <button className="hud-toggle" onClick={() => setOpen((o) => !o)} aria-label="Toggle debug overlay">
        {open ? '–' : '+'}
      </button>
      <div className="hud-row">
        <span className={`hud-fps ${fpsClass}`}>{perf.fps.toFixed(0)}</span>
        <span className="hud-dim">fps</span>
        <span className="hud-backend">{perf.backend}</span>
      </div>
      {open && (
        <>
          <dl className="hud-grid">
            <dt>frame</dt>
            <dd>{perf.frameMs.toFixed(1)} ms</dd>
            <dt>worst</dt>
            <dd>{perf.worstMs.toFixed(1)} ms</dd>
            <dt>gpu ms</dt>
            <dd>{perf.gpuMs === null ? 'n/a' : perf.gpuMs.toFixed(2)}</dd>
            <dt>post</dt>
            <dd>{perf.post}</dd>
            <dt>quality</dt>
            <dd>{perf.quality}</dd>
            <dt>draws</dt>
            <dd>{perf.drawCalls}</dd>
            <dt>tris</dt>
            <dd>{perf.triangles.toLocaleString()}</dd>
            <dt>size</dt>
            <dd>
              {perf.width}×{perf.height} @{perf.dpr.toFixed(2)}
            </dd>
            {perf.gpuLabel && (
              <>
                <dt>gpu</dt>
                <dd>{perf.gpuLabel}</dd>
              </>
            )}
            <dt>tone</dt>
            <dd>{flags.tone}</dd>
            <dt>errors</dt>
            <dd className={perf.errors.length ? 'bad' : ''}>{perf.errors.length}</dd>
          </dl>
          {perf.qualityLog.length > 0 && <pre className="hud-log">{perf.qualityLog.slice(-4).join('\n')}</pre>}
          {perf.errors.length > 0 && (
            <pre className="hud-errors">{perf.errors.slice(-3).join('\n')}</pre>
          )}
          <div className="hud-actions">
            <button onClick={copy}>{copied ? 'copied' : 'copy report'}</button>
            <a href={link({ webgl: flags.forceWebGL ? null : '1' })}>
              {flags.forceWebGL ? 'try WebGPU' : 'force WebGL 2'}
            </a>
            <a href={link({ stress: flags.stress ? null : '600' })}>{flags.stress ? 'stress off' : 'stress 600'}</a>
            <a href={link({ quality: fixedLevel === null ? '0' : null, scale: null, msaa: null, bloomres: null })}>
              {fixedLevel === null ? 'quality: fixed L0' : 'quality: auto'}
            </a>
            <a href={link({ post: flags.post ? '0' : null })}>{flags.post ? 'post off' : 'post on'}</a>
            {flags.post && (
              <a href={link({ bloomsrc: flags.bloomSource === 'masked' ? 'all' : null })}>
                {flags.bloomSource === 'masked' ? 'bloom: all' : 'bloom: masked'}
              </a>
            )}
          </div>
        </>
      )}
    </div>
  )
}
